import {
  type CalendarEvent,
  type CalendarLessonArrangement,
  type CalendarSchoolKind,
  type Classroom,
  calendarLessonArrangement,
  calendarWithPublicHolidays,
  hongKongDate,
  isSchoolCalendarEvent,
  type SchoolCalendarChange,
  type SchoolCalendarSelection,
  type SchoolYear,
  schoolCalendarChanges,
  schoolCalendarSelection,
  schoolYearStart,
  schoolYears,
  type Workspace,
} from "@classroom/domain";
import { useEffect, useMemo, useRef, useState } from "react";
import CalendarArrangement from "./CalendarArrangement";
import { interfaceLocale, useTranslation } from "./i18n";
import { weekdayLabels } from "./i18n/labels";
import { useDialogFocus } from "./lib/accessibility";
import PublicHolidayNotice from "./PublicHolidayNotice";
import SelectMenu from "./SelectMenu";
import "./school-calendar-editor.css";

const markerKinds = [
  "schoolHoliday",
  "teacherDevelopment",
  "exam",
  "activity",
  "parentsDay",
  "specialClass",
  "specialDay",
] as const;
type MarkerKind = (typeof markerKinds)[number];
const arrangements: CalendarLessonArrangement[] = ["normal", "noClasses", "special"];
const defaultArrangement: Record<MarkerKind, CalendarLessonArrangement> = {
  schoolHoliday: "noClasses",
  teacherDevelopment: "noClasses",
  exam: "special",
  activity: "special",
  parentsDay: "special",
  specialClass: "special",
  specialDay: "normal",
};
const iso = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
const dateLabel = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(interfaceLocale(), {
    day: "numeric",
    month: "numeric",
    timeZone: "Asia/Hong_Kong",
  });
const overlaps = (event: CalendarEvent, date: string) => event.date <= date && (event.endDate || event.date) >= date;

export default function SchoolCalendarEditor({
  workspace,
  schoolYear,
  classes,
  events,
  readOnly = false,
  onSave,
  onSelectYear,
  toast,
}: {
  workspace: Workspace;
  schoolYear?: SchoolYear;
  classes: Classroom[];
  events: CalendarEvent[];
  readOnly?: boolean;
  onSave: (changes: SchoolCalendarChange[]) => Promise<void>;
  onSelectYear: (yearId: string) => Promise<void>;
  toast: (message: string) => void;
}) {
  const { t } = useTranslation("schoolCalendar");
  const { t: yearText } = useTranslation("schoolYears");
  const managed = events.filter(isSchoolCalendarEvent);
  const [editing, setEditing] = useState(false);
  const [base, setBase] = useState<CalendarEvent[]>([]);
  const [draft, setDraft] = useState<CalendarEvent[]>([]);
  const [history, setHistory] = useState<CalendarEvent[][]>([]);
  const [selection, setSelection] = useState<SchoolCalendarSelection | null>(null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [focusedMonth, setFocusedMonth] = useState<number | null>(null);
  const [dayDetail, setDayDetail] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [kind, setKind] = useState<MarkerKind>("schoolHoliday");
  const [name, setName] = useState<string>(t("kinds.schoolHoliday"));
  const [arrangement, setArrangement] = useState<CalendarLessonArrangement>("noClasses");
  const [scope, setScope] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const lastDayRef = useRef<HTMLButtonElement | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const dialogRef = useDialogFocus<HTMLElement>(
    Boolean(dayDetail),
    () => setDayDetail(null),
    () => lastDayRef.current,
  );
  const year =
    Number(schoolYear?.startDate?.slice(0, 4) || schoolYear?.name.slice(0, 4)) || schoolYearStart(hongKongDate());
  const displayed = editing ? draft : managed;
  const allEvents = useMemo(
    () =>
      calendarWithPublicHolidays(
        [...events.filter((event) => event.source?.kind === "official"), ...displayed],
        workspace.id,
      ),
    [events, displayed, workspace.id],
  );
  const classNames = new Map(classes.map((classroom) => [classroom.id, classroom.name]));
  const dirty = editing && schoolCalendarChanges(base, draft).length > 0;
  const pending = dirty || Boolean(editing && selection);
  const blocked = readOnly || saving;
  const selectedCount = selection
    ? Math.round((Date.parse(selection.end) - Date.parse(selection.start)) / 86400000) + 1
    : 0;
  const hoverSelection = selection?.anchor && hoveredDate ? schoolCalendarSelection(selection, hoveredDate) : null;
  const itemsOnDate = (date: string) => allEvents.filter((event) => overlaps(event, date));
  const kindOf = (event: CalendarEvent): CalendarSchoolKind => event.schoolKind || "specialDay";
  const scopeLabel = (event: CalendarEvent) =>
    event.classId ? classNames.get(event.classId) || event.classId : t("schoolWide");
  const rangeLabel = (start: string, end: string) =>
    start === end ? dateLabel(start) : t("range", { start: dateLabel(start), end: dateLabel(end) });

  useEffect(() => {
    if (editingId) nameRef.current?.focus();
  }, [editingId]);

  function beginEdit() {
    if (blocked) return;
    setBase(managed);
    setDraft(managed);
    setHistory([]);
    setEditing(true);
    setError("");
  }

  function clearSelection() {
    setSelection(null);
    setHoveredDate(null);
    setEditingId(null);
  }

  function changeDraft(next: CalendarEvent[], previous = draft) {
    setHistory((steps) => [...steps.slice(-19), previous]);
    setDraft(next);
    setError("");
  }

  function chooseKind(next: MarkerKind) {
    setKind(next);
    setName(t(`kinds.${next}`));
    setArrangement(defaultArrangement[next]);
  }

  function applyMarker() {
    if (!selection || blocked || !editing || !name.trim()) return;
    const previous = draft.find((event) => event.id === editingId);
    const now = new Date().toISOString();
    const event: CalendarEvent = {
      ...previous,
      id: previous?.id || `event_${crypto.randomUUID()}`,
      workspaceId: workspace.id,
      schoolYearId: schoolYear?.id,
      classId: scope || undefined,
      date: selection.start,
      endDate: selection.end === selection.start ? undefined : selection.end,
      allDay: true,
      timezone: "Asia/Hong_Kong",
      title: name.trim(),
      type: "school",
      schoolKind: kind,
      lessonArrangement: arrangement,
      note: note.trim() || undefined,
      source: previous?.source || { kind: "manual" },
      createdAt: previous?.createdAt || now,
      updatedAt: now,
    };
    changeDraft(previous ? draft.map((item) => (item.id === previous.id ? event : item)) : [...draft, event]);
    clearSelection();
  }

  function editEvent(event: CalendarEvent) {
    if (blocked || !isSchoolCalendarEvent(event)) return;
    if (!editing) beginEdit();
    const nextKind = markerKinds.find((value) => value === event.schoolKind) || "specialDay";
    setKind(nextKind);
    setName(event.title);
    setScope(event.classId || "");
    setNote(event.note || "");
    setArrangement(calendarLessonArrangement(event) || "normal");
    setSelection({ start: event.date, end: event.endDate || event.date, anchor: null });
    setEditingId(event.id);
    setHoveredDate(null);
    setDayDetail(null);
  }

  function deleteEvent(event: CalendarEvent) {
    if (blocked || !isSchoolCalendarEvent(event)) return;
    if (!editing) beginEdit();
    const previous = editing ? draft : managed;
    changeDraft(
      previous.filter((item) => item.id !== event.id),
      previous,
    );
    clearSelection();
    setDayDetail(null);
  }

  async function save() {
    if (blocked || selection || !editing) return;
    setSaving(true);
    setError("");
    try {
      await onSave(schoolCalendarChanges(base, draft));
      setEditing(false);
      setHistory([]);
      clearSelection();
      toast(t("saved"));
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setError(
        message === "school-calendar-conflict"
          ? t("conflict")
          : message === "invalid-school-calendar-change" || message === "invalid-calendar-arrangement"
            ? t("invalid")
            : t("saveError"),
      );
    } finally {
      setSaving(false);
    }
  }

  function renderMonth(index: number) {
    const month = ((index + 8) % 12) + 1,
      monthYear = month >= 9 ? year : year + 1;
    const first = new Date(Date.UTC(monthYear, month - 1, 1)).getUTCDay();
    const days = new Date(Date.UTC(monthYear, month, 0)).getUTCDate();
    const label = new Date(Date.UTC(monthYear, month - 1, 1)).toLocaleDateString(interfaceLocale(), {
      year: "numeric",
      month: "long",
      timeZone: "Asia/Hong_Kong",
    });
    return (
      <section
        className={`sc-month${focusedMonth === index ? " expanded" : ""}`}
        key={`${monthYear}-${month}`}
        aria-label={label}
      >
        <header>
          <button type="button" aria-label={t("zoomMonth", { month: label })} onClick={() => setFocusedMonth(index)}>
            {label}
          </button>
        </header>
        <div className="sc-week" aria-hidden="true">
          {weekdayLabels().map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="sc-dates">
          {Array.from({ length: 42 }, (_, cell) => {
            const day = cell - first + 1;
            if (day < 1 || day > days)
              return (
                <span
                  className="sc-blank"
                  key={new Date(Date.UTC(monthYear, month - 1, day)).toISOString()}
                  aria-hidden="true"
                />
              );
            const date = iso(monthYear, month, day),
              items = itemsOnDate(date);
            const selected = Boolean(selection && date >= selection.start && date <= selection.end);
            const hovered = Boolean(hoverSelection && date >= hoverSelection.start && date <= hoverSelection.end);
            const baseEvent = [...items].reverse().find((event) => event.type === "school");
            const description = items.length
              ? items
                  .map((event) => {
                    const lesson = calendarLessonArrangement(event);
                    return `${event.title}${lesson ? ` · ${t(`arrangements.${lesson}`)}` : ""}`;
                  })
                  .join("、")
              : t("noEvents");
            return (
              <button
                type="button"
                className={`sc-date${selected ? " selected" : ""}${hovered && !selected ? " range-preview" : ""}${baseEvent ? ` marked ${kindOf(baseEvent)}` : ""}${cell % 7 === 0 ? " sunday" : ""}`}
                key={date}
                data-calendar-date={date}
                aria-label={t("dateButton", { date, events: description })}
                aria-pressed={editing ? selected : undefined}
                disabled={saving}
                title={description}
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse" && selection?.anchor) setHoveredDate(date);
                }}
                onFocus={() => {
                  if (selection?.anchor) setHoveredDate(date);
                }}
                onClick={(event) => {
                  lastDayRef.current = event.currentTarget;
                  if (editing && !blocked) {
                    setSelection((current) => schoolCalendarSelection(current, date));
                    setHoveredDate(null);
                  } else setDayDetail(date);
                }}
              >
                <span className="sc-date-number">{day}</span>
                {focusedMonth === index ? (
                  <span className="sc-date-titles">
                    {items.slice(0, 2).map((event) => (
                      <span key={event.id}>{event.title}</span>
                    ))}
                  </span>
                ) : (
                  <span className="sc-date-marks" aria-hidden="true">
                    {items.slice(0, 2).map((event) => (
                      <span key={event.id} className={kindOf(event)}>
                        {event.type === "school" ? t(`short.${kindOf(event)}`) : "·"}
                      </span>
                    ))}
                  </span>
                )}
                {items.length > 2 && <span className="sc-more">{t("more", { count: items.length - 2 })}</span>}
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <section
      className="school-calendar-editor"
      aria-label={t("title")}
      data-save-state={saving ? "saving" : pending ? "dirty" : "saved"}
    >
      <header className="sc-header">
        <div className="sc-heading">
          <h2>{t("title")}</h2>
          <fieldset disabled={saving} className="sc-year-picker">
            <SelectMenu
              label={yearText("selector")}
              value={schoolYear?.id || ""}
              options={schoolYears(workspace).map((item) => ({ value: item.id, label: item.name }))}
              onChange={(value) => {
                void onSelectYear(value);
              }}
            />
          </fieldset>
        </div>
        <div className="sc-actions">
          <span role="status">
            {saving
              ? t("saving")
              : readOnly
                ? t("readOnly")
                : pending
                  ? t("unsaved")
                  : editing
                    ? t("editing")
                    : t("saved")}
          </span>
          {editing ? (
            <>
              <button
                type="button"
                disabled={blocked || !history.length}
                aria-label={t("undo")}
                onClick={() => {
                  const previous = history.at(-1);
                  if (!previous) return;
                  setDraft(previous);
                  setHistory((steps) => steps.slice(0, -1));
                  clearSelection();
                  setError("");
                }}
              >
                ↶
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setEditing(false);
                  setHistory([]);
                  clearSelection();
                  setError("");
                }}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                className="primary-action"
                disabled={blocked || Boolean(selection)}
                onClick={() => void save()}
              >
                {t("save")}
              </button>
            </>
          ) : (
            <button type="button" className="primary-action" disabled={blocked} onClick={beginEdit}>
              {t("edit")}
            </button>
          )}
        </div>
      </header>
      <PublicHolidayNotice years={[year, year + 1]} />
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="sc-layout">
        <fieldset className="sc-tools" disabled={blocked || !editing} aria-label={t("tools")}>
          <legend>{t("tools")}</legend>
          <div className="sc-palette">
            {markerKinds.map((value) => (
              <button
                type="button"
                key={value}
                className={`sc-kind ${value}`}
                aria-pressed={kind === value}
                onClick={() => chooseKind(value)}
              >
                <i aria-hidden="true" />
                {t(`kinds.${value}`)}
              </button>
            ))}
          </div>
          <label className="sc-field">
            {t("name")}
            <input
              ref={nameRef}
              aria-label={t("name")}
              value={name}
              maxLength={80}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <div className="sc-field">
            <span>{t("arrangement")}</span>
            <SelectMenu
              label={t("arrangement")}
              value={arrangement}
              options={arrangements.map((value) => ({ value, label: t(`arrangements.${value}`) }))}
              onChange={(value) => setArrangement(value as CalendarLessonArrangement)}
            />
          </div>
          <div className="sc-field">
            <span>{t("scope")}</span>
            <SelectMenu
              label={t("scope")}
              value={scope}
              options={[
                { value: "", label: t("schoolWide") },
                ...classes.map((item) => ({ value: item.id, label: item.name })),
              ]}
              onChange={setScope}
            />
          </div>
          <label className="sc-field">
            {t("note")}
            <textarea
              aria-label={t("note")}
              value={note}
              rows={2}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
        </fieldset>
        <div className="sc-calendar">
          <div className="sc-selection-bar">
            {focusedMonth !== null ? (
              <button type="button" onClick={() => setFocusedMonth(null)}>
                ← {t("backToYear")}
              </button>
            ) : (
              <span>{t("period")}</span>
            )}
            <span aria-live="polite">
              {editing
                ? selection?.anchor
                  ? t("chooseEnd")
                  : selection
                    ? t("selected", { count: selectedCount })
                    : t("chooseDates")
                : ""}
            </span>
          </div>
          <fieldset
            className={`sc-months${focusedMonth !== null ? " zoomed" : ""}`}
            aria-label={t("dates")}
            onPointerLeave={() => setHoveredDate(null)}
          >
            {focusedMonth !== null
              ? renderMonth(focusedMonth)
              : Array.from({ length: 12 }, (_, index) => renderMonth(index))}
          </fieldset>
          <details className="sc-events-list">
            <summary>
              {t("eventsList")} · {displayed.length}
            </summary>
            {[...displayed]
              .sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title, interfaceLocale()))
              .map((event) => (
                <button
                  type="button"
                  className="sc-list-event"
                  key={event.id}
                  onClick={(click) => {
                    lastDayRef.current = click.currentTarget;
                    setDayDetail(event.date);
                  }}
                >
                  <time dateTime={event.date}>{rangeLabel(event.date, event.endDate || event.date)}</time>
                  <span>{event.title}</span>
                  <small>{scopeLabel(event)}</small>
                  <CalendarArrangement event={event} />
                </button>
              ))}
          </details>
          {editing && (
            <footer className="sc-apply-bar">
              <div>
                <strong>{selection ? rangeLabel(selection.start, selection.end) : t("chooseDates")}</strong>
                <small>
                  {name} · {scope ? classNames.get(scope) : t("schoolWide")}
                </small>
              </div>
              <div className="sc-actions">
                <button type="button" disabled={blocked || !selection} onClick={clearSelection}>
                  {t("clear")}
                </button>
                <button
                  type="button"
                  disabled={saving || !selection}
                  onClick={() => {
                    if (selection) setDayDetail(selection.start);
                  }}
                >
                  {t("inspect")}
                </button>
                <button
                  type="button"
                  className="primary-action"
                  disabled={blocked || !selection || !name.trim()}
                  onClick={applyMarker}
                >
                  {editingId ? t("update") : t("apply")}
                </button>
              </div>
            </footer>
          )}
        </div>
      </div>
      {dayDetail && (
        // biome-ignore lint/a11y/noStaticElementInteractions: backdrop clicks dismiss the keyboard-accessible dialog.
        <div
          className="calendar-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDayDetail(null);
          }}
        >
          <section
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={t("dayItems", { date: dayDetail })}
            className="paper-card calendar-modal sc-day-dialog"
          >
            <header>
              <h2>
                {new Date(`${dayDetail}T12:00:00Z`).toLocaleDateString(interfaceLocale(), {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  weekday: "long",
                  timeZone: "Asia/Hong_Kong",
                })}
              </h2>
              <button type="button" aria-label={t("close")} onClick={() => setDayDetail(null)}>
                ×
              </button>
            </header>
            <div className="sc-day-items">
              {itemsOnDate(dayDetail).map((event) => (
                <article key={event.id}>
                  <h3>{event.title}</h3>
                  <p>
                    {t(`kinds.${kindOf(event)}`)} · {scopeLabel(event)} <CalendarArrangement event={event} />
                  </p>
                  <small>{rangeLabel(event.date, event.endDate || event.date)}</small>
                  {event.note && <p>{event.note}</p>}
                  {event.source?.kind === "official" ? (
                    <small>{t("official")}</small>
                  ) : (
                    isSchoolCalendarEvent(event) &&
                    !readOnly && (
                      <div className="sc-actions">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => editEvent(event)}
                          aria-label={t("editItem", { name: event.title })}
                        >
                          {t("editMarker")}
                        </button>
                        <button
                          type="button"
                          className="danger-text"
                          disabled={saving}
                          onClick={() => deleteEvent(event)}
                          aria-label={t("deleteItem", { name: event.title })}
                        >
                          {t("delete")}
                        </button>
                      </div>
                    )
                  )}
                </article>
              ))}
            </div>
            {!readOnly && (
              <footer className="sc-actions">
                <button
                  type="button"
                  className="primary-action"
                  disabled={saving}
                  onClick={() => {
                    if (!editing) beginEdit();
                    setSelection({ start: dayDetail, end: dayDetail, anchor: dayDetail });
                    setEditingId(null);
                    setDayDetail(null);
                  }}
                >
                  {t("add")}
                </button>
              </footer>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
